// What a scan sounds and feels like, so the courier knows the result without looking:
//   ok     one short high beep, one short buzz
//   warn   two middle beeps, two short buzzes   (already scanned, scan the piece labels)
//   error  one long low tone, one long buzz     (not yours, unknown, wrong parcel, not in the hub)
export type ScanTone = 'ok' | 'warn' | 'error';

let ctx: AudioContext | null = null;

/** Phones play sound only after a tap: call this from a tap (start camera, any button) once. */
export function unlockScanSound() {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    ctx = null;
  }
}

function tone(freq: number, start: number, length: number, volume = 0.15) {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.value = freq;
  gain.gain.value = volume;
  osc.connect(gain).connect(ctx.destination);
  const t = ctx.currentTime + start;
  osc.start(t);
  osc.stop(t + length);
}

const VIBRATION: Record<ScanTone, number[]> = { ok: [70], warn: [60, 80, 60], error: [400] };

export function scanFeedback(kind: ScanTone) {
  // for tests and support: the last feedback given
  document.documentElement.dataset.lastScanTone = kind;
  try {
    navigator.vibrate?.(VIBRATION[kind]);
  } catch {
    /* no vibration */
  }
  try {
    unlockScanSound();
    if (kind === 'ok') tone(1400, 0, 0.09);
    else if (kind === 'warn') {
      tone(700, 0, 0.09);
      tone(700, 0.15, 0.09);
    } else tone(220, 0, 0.45, 0.2);
  } catch {
    /* no sound */
  }
}

const LOAD_TONE: Record<string, ScanTone> = {
  LOADED: 'ok',
  ALREADY_LOADED: 'warn',
  SCAN_PIECE_LABELS: 'warn',
  ALREADY_OUT: 'error',
  NOT_YOURS: 'error',
  NOT_IN_HUB: 'error',
  UNKNOWN: 'error',
};
const DOOR_TONE: Record<string, ScanTone> = {
  SCANNED: 'ok',
  ALREADY_SCANNED: 'warn',
  SCAN_PIECE_LABELS: 'warn',
  OTHER_PARCEL: 'error',
  UNKNOWN: 'error',
};

export const loadTone = (code: string): ScanTone => LOAD_TONE[code] ?? 'error';
export const doorTone = (code: string): ScanTone => DOOR_TONE[code] ?? 'error';
